import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Button, CircularProgress, Divider, FormControlLabel, Grid, Switch } from '@material-ui/core';
import FormInput from '../ReactHookForm/FormInput/FormInput';
import SectionHeader from '../ReactHookForm/SectionHeader';
import { useHistory } from 'react-router';
import { Controller } from 'react-hook-form';
import ColoredCheckbox from '../ColoredInputs/ColoredCheckbox';
import ReactSelectWrapper from '../QaOrderReview/ReactSelectWrapper';
import { getClientCallConfiguration, saveClientCallConfiguration } from '../../services';
import { orderLineStatusLookupCodes } from '../../enums';

const styles = theme => ({
  gridContainer: {
    // flex: 1,
    // alignSelf: 'stretch',
  },
  form: {
    width: '100%',
  },
});

const FORM_FIELDS = {
  MIN_TIME_OF_DAY: 'minTimeOfDay',
  MAX_TIME_OF_DAY: 'maxTimeOfDay',
  DAYS_OF_WEEK: 'daysOfWeek',
  AUTO_CALL: 'autoCall',
  ORDER_LINE_STATUS_LOOKUP_ID: 'orderLineStatusLookupId',
  SUCCESS_ORDER_LINE_STATUS_LOOKUP_ID: 'successOrderLineStatusLookupId',
};

// Day of week options
const DAYS_OF_WEEK_OPTIONS = [
  { value: "0", label: 'Monday' },
  { value: "1", label: 'Tuesday' },
  { value: "2", label: 'Wednesday' },
  { value: "3", label: 'Thursday' },
  { value: "4", label: 'Friday' },
  { value: "5", label: 'Saturday' },
  { value: "6", label: 'Sunday' },
];

const ORDER_LINE_STATUS_LOOKUP_ID_OPTIONS = Object.entries(orderLineStatusLookupCodes).map(([key, value]) => ({
  value: value,
  label: key
}));

const formValidationSchema = Yup.object().shape({
  [FORM_FIELDS.MIN_TIME_OF_DAY]: Yup.date()
    .required('Minimum call time is required')
    .default(''),
  [FORM_FIELDS.MAX_TIME_OF_DAY]: Yup.date()
    .required('Maximum call time is required')
    .default(''),
  [FORM_FIELDS.DAYS_OF_WEEK]: Yup.array()
    .of(
      Yup.string().required('Day is required')
    )
    .min(1, 'At least one day must be selected')
    .required('Days of week are required')
    .default([]),
  [FORM_FIELDS.AUTO_CALL]: Yup.boolean()
    .default(false),
  [FORM_FIELDS.ORDER_LINE_STATUS_LOOKUP_ID]: Yup.number()
    .min(1, 'Order Line Status Lookup Id is required')
    .required('Order Line Status Lookup Id is required')
    .default(36),
  [FORM_FIELDS.SUCCESS_ORDER_LINE_STATUS_LOOKUP_ID]: Yup.number()
    .min(1, 'Success Order Line Status Lookup Id is required')
    .required('Success Order Line Status Lookup Id is required')
    .default(37),
});

export const AutomatedCallConfigurationTab = withStyles(styles)(
  ({ classes, match, pageMode, callConfigId, useCardContainer, clientId, ...rest }) => {
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();

    const formMethods = useForm({
      resolver: yupResolver(formValidationSchema),
      reValidateMode: 'onSubmit',
      defaultValues: {
        [FORM_FIELDS.ORDER_LINE_STATUS_LOOKUP_ID]: 36,
        [FORM_FIELDS.SUCCESS_ORDER_LINE_STATUS_LOOKUP_ID]: 37,
      },
    });

    const { setValue, getValues } = formMethods;

    console.log('getValues', getValues());


    const onSubmitInvalid = async errors => {
      console.log('Form validation errors:', errors);
    };

    const onSubmitValid = async form => {
      // Get timezone offset in hours
      const timezoneOffsetHours = new Date().getTimezoneOffset() / 60;

      const minTime = new Date(form.minTimeOfDay);
      const maxTime = new Date(form.maxTimeOfDay);

      minTime.setHours(minTime.getHours());
      maxTime.setHours(maxTime.getHours());


      const result = {
        ...form,
        minTimeOfDay: minTime,
        maxTimeOfDay: maxTime,
        timezoneOffset: timezoneOffsetHours,
        clientId,
      }
      console.log('result', result);
      try {
        const response = await saveClientCallConfiguration(result);
        console.log('Save response:', response);
        history.goBack();
      } catch (error) {
        console.error('Error saving call configuration:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const loadCallConfiguration = async () => {
      let clientConfig = await getClientCallConfiguration(clientId);

      if (clientConfig) {
        // Get current local timezone offset
        const now = new Date();
        const timezoneOffset = now.getTimezoneOffset();
        const hours = Math.abs(Math.floor(timezoneOffset / 60));
        const minutes = Math.abs(timezoneOffset % 60);
        const offsetString = `${timezoneOffset <= 0 ? '+' : '-'}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        // Replace timezone offset with local timezone offset
        const formattedMinTime = clientConfig.minTimeOfDay 
          ? clientConfig.minTimeOfDay.replace(/[+-]\d{2}:\d{2}$/, offsetString)
          : '';
        const formattedMaxTime = clientConfig.maxTimeOfDay 
          ? clientConfig.maxTimeOfDay.replace(/[+-]\d{2}:\d{2}$/, offsetString)
          : '';

        formMethods.reset({
          ...clientConfig,
          minTimeOfDay: formattedMinTime,
          maxTimeOfDay: formattedMaxTime,
        });
      }
    };

    useEffect(() => {
      loadCallConfiguration();
    }, [clientId]);


    return (
      <FormProvider {...formMethods} schema={formValidationSchema}>
        <form onSubmit={formMethods.handleSubmit(onSubmitValid, onSubmitInvalid)} className={classes.form}>
          <Grid
            container
            direction="row"
            className={classes.gridContainer}
            spacing={2}
          >
            {/* Call Time Settings */}
            <SectionHeader title="Call Time Settings" />

            <Grid item xs={4} style={{ textAlign: 'left' }}>
              <FormInput
                type="time"
                name={FORM_FIELDS.MIN_TIME_OF_DAY}
                label="Minimum Call Time"
              />
            </Grid>
            <Grid item xs={4} style={{ textAlign: 'left' }}>
              <FormInput
                type="time"
                name={FORM_FIELDS.MAX_TIME_OF_DAY}
                label="Maximum Call Time"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Days of Week */}
            <SectionHeader title="Call Schedule" />

            <Grid item xs={12}>
              <FormInput
                type="multiselect"
                name={FORM_FIELDS.DAYS_OF_WEEK}
                label="Days of Week"
                options={DAYS_OF_WEEK_OPTIONS}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* orderlinestatuslookupid and successorderlinestatuslookupid Settings */}
            {/* <SectionHeader title="Order Line Status Lookup Id Settings" />

            <Grid item xs={3}>
              <FormInput
                type="select"
                name={FORM_FIELDS.ORDER_LINE_STATUS_LOOKUP_ID}
                label="Order Line Status Lookup Id"
                options={ORDER_LINE_STATUS_LOOKUP_ID_OPTIONS}
              />
            </Grid>

            <Grid item xs={3}>
              <FormInput
                type="select"
                name={FORM_FIELDS.SUCCESS_ORDER_LINE_STATUS_LOOKUP_ID}
                label="Success Order Line Status Lookup Id"
                options={ORDER_LINE_STATUS_LOOKUP_ID_OPTIONS}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid> */}

            {/* Auto Call Settings */}
            <SectionHeader title="Auto Call Settings" />

            <Grid item xs={12} style={{ textAlign: 'left' }}>
              <Controller
                name={FORM_FIELDS.AUTO_CALL}
                control={formMethods.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <ColoredCheckbox
                        checked={field.value === true}
                        onChange={e => field.onChange(e.target.checked === true)}
                        name={FORM_FIELDS.AUTO_CALL}
                      />
                    }
                    label="Enable Automated Calling"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12} container style={{ marginTop: 50 }}>
              <Button
                style={{ backgroundColor: '#315382', color: '#fff' }}
                variant="contained"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress style={{ fill: '#fff', color: '#fff', marginRight: 10 }} size={25} />
                    Saving
                  </>
                ) : (
                  'Save Configuration'
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    );
  },
);

AutomatedCallConfigurationTab.propTypes = {
  pageMode: PropTypes.string,
  callConfigId: PropTypes.string,
  useCardContainer: PropTypes.bool,
};

AutomatedCallConfigurationTab.defaultProps = {
  pageMode: 'EDIT',
  callConfigId: null,
  useCardContainer: false,
};

export default AutomatedCallConfigurationTab; 